/*
 * Notice period is a whole number of DAYS. The unit is a property of the field,
 * so nothing has to state it and nothing can disagree about it.
 *
 * Why days rather than free text: `candidate.noticePeriod` is a STRING column
 * that two screens wrote in two shapes — the assistant panel offered labels
 * ("15 days", "2 months") while the career-profile modal was a plain text box.
 * Live data ended up holding "Immediate", "1 month", "60 days" and a bare "30"
 * that nobody can now interpret. Days also makes the value comparable, which a
 * label never can be: "can join within a month" is `noticePeriod <= 30`, where
 * '15 days' vs '1 month' cannot be ordered in SQL at all.
 *
 * The column stays STRING (no schema migration) and holds the canonical digits
 * — "0", "15", "30" — so option values are strings here too, which is also what
 * a MUI Select needs to match a value read back from the API.
 *
 * Labels carry the month equivalent because candidates think in months even
 * when the stored answer is days.
 */
export const NOTICE_PERIOD_OPTIONS = [
    { value: '0', label: 'Immediate' },
    { value: '15', label: '15 days' },
    { value: '30', label: '30 days (1 month)' },
    { value: '45', label: '45 days (1.5 months)' },
    { value: '60', label: '60 days (2 months)' },
    { value: '90', label: '90 days (3 months)' },
];

/*
 * The one way a notice period is rendered. Every screen showing one calls this
 * — a screen that appends its own unit is how "2 months days" reached the
 * recruiter view.
 *
 * Values written before the days convention ("1 month", "Immediate") are not
 * numeric; those are returned unchanged rather than shown as NaN, so an
 * un-backfilled row still reads correctly.
 */
export const formatNoticePeriod = (value) => {
    if (value === null || value === undefined || value === '') return '';

    const days = Number(value);
    if (!Number.isFinite(days)) return String(value);
    if (days <= 0) return 'Immediate';

    const known = NOTICE_PERIOD_OPTIONS.find((option) => option.value === String(days));
    return known ? known.label : `${days} day${days === 1 ? '' : 's'}`;
};

/*
 * Options for a picker, with the candidate's saved value guaranteed to be among
 * them. A bare list would show a legacy or unusual value as no selection at
 * all, so opening the form and saving would silently wipe the answer.
 */
export const noticePeriodOptionsFor = (value) => {
    if (value === null || value === undefined || value === '') return NOTICE_PERIOD_OPTIONS;
    const current = String(value);
    if (NOTICE_PERIOD_OPTIONS.some((option) => option.value === current)) return NOTICE_PERIOD_OPTIONS;
    return [...NOTICE_PERIOD_OPTIONS, { value: current, label: formatNoticePeriod(current) }];
};
